/**
 * ReactivateResellerDialog (TMMPDM-0026, tmm-api 0090): reactivate a DEACTIVATED reseller with a
 * required 1–500 reason. Its tenants are not moved back and its shortfall charges stay open. Success, 409
 * `RESELLER_STANDING_CONFLICT` and 502 `SAAS_AUTH_OUTCOME_UNKNOWN` each close the dialog and hand
 * the parent a re-read; nothing is re-sent automatically.
 * TMMPDM-0041 (tmm-api 0102): also offered for a reseller saas-auth holds suspended in another
 * standing. The consequence copy is shared across standing states.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isOutcomeUnknown } from '@/api/errors';
import type { ResellerDto, ResellerStandingState } from '@/api/tmm/types';

/** `ResellerReactivateRequest.reason`: 1–500 characters (TMMAPI-0090). */
const REACTIVATE_REASON_MAX = 500;

interface ReactivateResellerDialogProps {
  open: boolean;
  reseller: Pick<ResellerDto, 'id' | 'name'>;
  /** The standing it is reactivated from (`null` while unknown). */
  standingState?: ResellerStandingState | null;
  onClose: () => void;
  /** 200 — the parent says so and re-reads the reseller and its standing. */
  onReactivated: () => void;
  /** 409 `RESELLER_STANDING_CONFLICT` — the parent says so and re-reads. */
  onConflict: () => void;
  /** 409 ordinary suspension: show the status-toggle guidance and re-read. */
  onNotDeactivated: () => void;
  /** 502 `SAAS_AUTH_OUTCOME_UNKNOWN` — the parent re-reads and says so; never re-POST. */
  onOutcomeUnknown: () => void;
}

export function ReactivateResellerDialog({ open, ...rest }: ReactivateResellerDialogProps) {
  const { t } = useCopy();
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) rest.onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t('resellers.reactivate.title', 'Reactivate {{name}}?', { name: rest.reseller.name })}
      </DialogTitle>
      {open ? <ReactivateForm {...rest} busy={busy} setBusy={setBusy} /> : null}
    </Dialog>
  );
}

function ReactivateForm({
  reseller,
  onClose,
  onReactivated,
  onConflict,
  onNotDeactivated,
  onOutcomeUnknown,
  busy,
  setBusy,
}: Omit<ReactivateResellerDialogProps, 'open'> & {
  busy: boolean;
  setBusy: (busy: boolean) => void;
}) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trimmed = reason.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= REACTIVATE_REASON_MAX;

  const handleConfirm = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.reactivateReseller(reseller.id, trimmed);
      onClose();
      onReactivated();
    } catch (err) {
      if (err instanceof ApiError && String(err.code) === 'RESELLER_NOT_DEACTIVATED') {
        onClose();
        onNotDeactivated();
        return;
      }
      if (err instanceof ApiError && String(err.code) === 'RESELLER_STANDING_CONFLICT') {
        onClose();
        onConflict();
        return;
      }
      if (isOutcomeUnknown(err)) {
        onClose();
        onOutcomeUnknown();
        return;
      }
      setError(
        errorCopy(err, t, t('resellers.reactivate.error', 'Could not reactivate the reseller.')),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2">
            {t(
              'resellers.reactivate.body',
              "Reactivating restores this partner's administrators and resumes payouts from the next settlement. Settlements already retained stay retained.",
            )}
          </Typography>
          <TextField
            label={t('resellers.reactivate.reason', 'Reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={t('resellers.reactivate.counter', '{{count}}/{{max}}', {
              count: reason.length,
              max: REACTIVATE_REASON_MAX,
            })}
            slotProps={{ htmlInput: { maxLength: REACTIVATE_REASON_MAX } }}
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void handleConfirm()} busy={busy} disabled={!valid}>
          {t('resellers.reactivate.confirm', 'Reactivate')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
